import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import OptionActions from '../../../reducers/Options'
import { trackEvent } from '../../../utils/segmentService'
import {
  TRACKING_EVENTS,
  FILTER_ACTIONS,
  PLATFORMS,
  FILTER_TYPES,
  FILTER_LABELS
} from '../../../constants/tracking'

export const useFilters = ({ deferClear = false } = {}) => {
  const dispatch = useDispatch()
  const {
    genres,
    markets,
    packages,
    release_types,
    versions,
    moods,
    instruments,
    activeFilters
  } = useSelector((state) => state.options)

  const [localSelectedFilters, setLocalSelectedFilters] = useState([])

  // Sync local state with activeFilters when they change from outside
  useEffect(() => {
    setLocalSelectedFilters(activeFilters)
  }, [activeFilters])

  // Track filter interactions
  const trackFilterEvent = (
    action,
    filterType,
    filterLabel,
    platform = PLATFORMS.MOBILE
  ) => {
    const eventData = {
      action, // FILTER_ACTIONS.SELECT, FILTER_ACTIONS.REMOVE, FILTER_ACTIONS.CLEAR_ALL
      filter_type: filterType,
      filter_label: filterLabel ? String(filterLabel) : '',
      platform,
      total_active_filters: String(localSelectedFilters.length)
    }

    // Track with Segment (and Statsig via statsigCustom property)
    trackEvent(TRACKING_EVENTS.FILTER_INTERACTION, eventData)
  }

  // Sort options with selected items first, then alphabetically
  const sortFilterOptions = (options, filterType) => {
    return [...options].sort((a, b) => {
      const aIsSelected = localSelectedFilters.some(
        (f) => f.type === filterType && f.id === a.id
      )
      const bIsSelected = localSelectedFilters.some(
        (f) => f.type === filterType && f.id === b.id
      )

      // First sort by selection status (selected items first)
      if (bIsSelected !== aIsSelected) {
        return bIsSelected - aIsSelected
      }

      // Then sort alphabetically
      return a.name.localeCompare(b.name)
    })
  }

  const handleFilterToggle = (type, options) => (e, label, value) => {
    const filterOption = options.find((option) => option.id === value)
    if (!filterOption) return

    const exists = localSelectedFilters.find(
      (f) => f.type === type && f.id === filterOption.id
    )

    // Track the event
    trackFilterEvent(
      exists ? FILTER_ACTIONS.REMOVE : FILTER_ACTIONS.SELECT,
      type,
      filterOption.name,
      PLATFORMS.MOBILE
    )

    setLocalSelectedFilters((prev) => {
      const isSelected = prev.find(
        (f) => f.type === type && f.id === filterOption.id
      )
      if (isSelected) {
        return prev.filter(
          (f) => !(f.type === type && f.id === filterOption.id)
        )
      } else {
        return [
          ...prev,
          {
            type,
            id: filterOption.id,
            label: filterOption.name
          }
        ]
      }
    })
  }

  // Handle single selection filters (mood, instrument) - radio button behavior
  const handleSingleFilterToggle = (type, options) => (e, label, value) => {
    const filterOption = options.find((option) => option.id === value)
    if (!filterOption) return

    const exists = localSelectedFilters.find(
      (f) => f.type === type && f.id === filterOption.id
    )

    // Track the event
    trackFilterEvent(
      exists ? FILTER_ACTIONS.REMOVE : FILTER_ACTIONS.SELECT,
      type,
      filterOption.name,
      PLATFORMS.MOBILE
    )

    setLocalSelectedFilters((prev) => {
      const isSelected = prev.find(
        (f) => f.type === type && f.id === filterOption.id
      )

      if (isSelected) {
        // If clicking the same filter, remove it (deselect)
        return prev.filter(
          (f) => !(f.type === type && f.id === filterOption.id)
        )
      } else {
        // Remove any existing filter of this type and add the new one
        const filteredPrev = prev.filter((f) => f.type !== type)
        return [
          ...filteredPrev,
          {
            type,
            id: filterOption.id,
            label: filterOption.name
          }
        ]
      }
    })
  }

  const clearAllFilters = () => {
    // Track clear all event
    trackFilterEvent(
      FILTER_ACTIONS.CLEAR_ALL,
      FILTER_TYPES.ALL,
      FILTER_LABELS.ALL_FILTERS,
      PLATFORMS.MOBILE
    )

    if (!deferClear) dispatch(OptionActions.clearAllFilters())
    setLocalSelectedFilters([])
  }

  const applyFilters = () => {
    dispatch(OptionActions.setFilters(localSelectedFilters))
  }

  const resetLocalFilters = () => {
    setLocalSelectedFilters(activeFilters)
  }

  // Check if there are filter changes
  const hasFilterChanges =
    JSON.stringify(localSelectedFilters) !== JSON.stringify(activeFilters)

  return {
    // Data
    genres,
    markets,
    packages,
    release_types,
    versions,
    moods,
    instruments,
    activeFilters,
    localSelectedFilters,

    // Computed states
    hasFilterChanges,

    // Functions
    sortFilterOptions,
    handleFilterToggle,
    handleSingleFilterToggle,
    clearAllFilters,
    applyFilters,
    resetLocalFilters
  }
}
